import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { instance, pathname } from "./ActionCreators";
import axios from "axios";
import { RootState } from "../store";
import notifySuccess from "utils/Notifications/Success";
import copyName from "utils/copyName/copyName";
import Error from "../../utils/Notifications/Error";
import {
    getTickerSettings,
    resetGroup,
    resetTickerSettings,
    setSelectedGroup,
    setSelectedTicker,
    updateTickerName,
} from "./SettingsSlice";

type Group = { name: string; id: number };
type Tickers = { name: string; isNew: boolean }[];

type IState = {
    groupsLoading: boolean;
    tickersLoading: boolean;
    error: null | string;
    groups: Group[];
    activeGroupTickers: Tickers;
    defaultTickers: string[];
};

const initialState: IState = {
    groupsLoading: false,
    tickersLoading: false,
    error: null,
    groups: [],
    activeGroupTickers: [],
    defaultTickers: []
};

export const getGroups = createAsyncThunk(
    "get/groups",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(pathname + "api/groups");
            return response.data.map((name: string, index: number) => {
                return {
                    name,
                    id: index,
                };
            });
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const getTickersByGroup = createAsyncThunk(
    "get/tickersByGroup",
    async (groupName: string, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                pathname + `api/ticker/${groupName}`
            );
            return response.data.map((name) => ({ name: name, isNew: false }));
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const getDefaultTickers = createAsyncThunk(
    "get/defaultTickers",
    async (_, { rejectWithValue }) => {
        try {
            const response = await instance.get(
                pathname + `api/ticker/default`
            );
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

function rtrim(str) {
    if (!str) return str;
    return str.replace(/\s+$/g, "");
}

export const createGroup = createAsyncThunk(
    "create/group",
    async (
        { name, type }: { name: string; type: string | undefined },
        { rejectWithValue, dispatch, getState }
    ) => {
        try {
            const { settings, groups } = getState() as RootState;

            if (
                groups.groups.filter((it: { name: string }) => it.name === name)
                    .length === 0
            ) {
                let extraSpaceName = rtrim(name);
                const groupName =
                    type === "copy"
                        ? `${extraSpaceName} - копия 1`
                        : extraSpaceName;

                const body = {
                    name: groupName,
                };

                const response = await instance.post(
                    pathname + "api/groups",
                    body
                );
                notifySuccess("Группа успешно создана");
                dispatch(getGroups());
                return response.data;
            } else {
                dispatch(getGroups());
                Error("Группа с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const copyGroup = createAsyncThunk(
    "copy/group",
    async (
        {
            groupCopyName,
            groupName,
        }: { groupCopyName: string; groupName: string },
        { dispatch }
    ) => {
        try {
            const body = {
                group_name: groupName,
                target_group_name: groupCopyName,
            };
            const response = await instance.post(
                pathname + "api/copy_group",
                body
            );
            notifySuccess(
                `Создана копия группы ${groupName} с именем ${groupCopyName}`
            );

            dispatch(getGroups());
            return response;
        } catch (e) {}
    }
);

export const renameGroup = createAsyncThunk(
    "rename/group",
    async (_name: string, { rejectWithValue, dispatch, getState }) => {
        const name = _name.slice().trim();
        const { settings, groups } = getState() as RootState;
        const { activeGroup } = settings;
        try {
            if (
                groups.groups.filter((it: { name: string }) => it.name === name)
                    .length === 0
            ) {
                const body = {
                    group_name: name,
                };
                const response = await instance.patch(
                    pathname + `api/groups/${activeGroup.name}`,
                    body
                );
                notifySuccess("Группа успешно переименована");
                dispatch(getGroups());
                dispatch(setSelectedGroup({ name, id: activeGroup?.id }));
                dispatch(getTickersByGroup(name));
                return response.data;
            } else {
                dispatch(getGroups());
                dispatch(setSelectedGroup(activeGroup));
                Error("Группа с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const deleteGroup = createAsyncThunk(
    "delete/tabs",
    async (name: string, { rejectWithValue, dispatch }) => {
        try {
            let apiString = `api/groups/${name} `;
            // let extraSpaceName = name[name.length - 1] === " " ? name.slice(0, name.length - 1) : name
            const response = await instance.delete(pathname + apiString);
            notifySuccess("Группа удалена");
            dispatch(resetGroup());
            dispatch(resetGroupTickers());
            dispatch(getGroups());
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                if (e.response.data.name) {
                    Error(e.response.data.name);
                }
                return rejectWithValue(e.message);
            }
        }
    }
);

export const applyGroup = createAsyncThunk(
    "apply/group",
    async (groupName: string, { rejectWithValue, dispatch }) => {
        try {
            const body = {
                group_name: groupName,
            };
            const response = await instance.post(
                pathname + "api/apply_group",
                body
            );
            dispatch(getGroups());
            notifySuccess("Группа успешно применена");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const applyTicker = createAsyncThunk(
    "apply/ticker",
    async (tickerName: string | null, { rejectWithValue, getState, dispatch }) => {
        const { settings, groups } = getState() as RootState;
        // const {name: groupName} = settings.activeGroup
        const { activeTicker: ticker, activeGroup: group } = settings;
        const { defaultTickers } = groups
        const isTickerNew = defaultTickers.includes(ticker.name) ? false : true
        
        const actualTickerName = tickerName ?? ticker.name;

        try {
            const body = {
                group_name: group.name,
                ticker_name: actualTickerName,
                is_new: isTickerNew,
            };
            const response = await instance.post(
                pathname + "api/apply/ticker",
                body
            );
            dispatch(getDefaultTickers())
            notifySuccess("Тикер успешно применён");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const copyTicker = createAsyncThunk(
    "copy/ticker",
    async (tickerName: string, { rejectWithValue, getState }) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const { data: dataTicker } = await instance.get(
                pathname + `api/ticker/${tickerName}?group_name=${groupName}`
            );
            const { data: nameTickers } = await instance.get(
                pathname + `api/ticker/${groupName}`
            );
            const fullName = dataTicker.CP[1][1].split("~");
            const newName = copyName(fullName[0], nameTickers);
            const body = {
                group_name: groupName,
                ticker_name: newName,
            };
            const response = await instance.post(
                `${pathname}api/duplicate_ticker`,
                body
            );
            notifySuccess("Тикер успешно скопирован");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const renameTicker = createAsyncThunk(
    "rename/ticker",
    async (_name: string, { rejectWithValue, getState, dispatch }) => {
        const name = _name.slice().trim();
        const { settings, groups } = getState() as RootState;
        const {
            activeTicker,
            activeGroup,
            settings: tickerSettings,
        } = settings;
        try {
            if (
                groups.activeGroupTickers.filter((it) => it.name === name)
                    .length === 0
            ) {
                const body = {
                    ticker_name: name,
                };
                const response = await instance.patch(
                    pathname +
                        `api/ticker/${activeTicker.name}?group_name=${activeGroup.name}`,
                    body
                );
                notifySuccess("Тикер успешно переименован");
                dispatch(getGroups());
                dispatch(
                    setSelectedGroup({
                        name: activeGroup?.name,
                        id: activeGroup?.id,
                    })
                );
                dispatch(setSelectedTicker({ name }));
                dispatch(getTickersByGroup(activeGroup?.name));
                /*
                dispatch(updateTickerName({
                    old_name: activeTicker?.name,
                    new_name: name
                }))
                */
                const timeout = setTimeout(() => {
                    dispatch(getTickerSettings());
                    return clearTimeout(timeout);
                }, 200);
                return response.data;
            } else {
                Error("Тикер с таким названием уже существует");
            }
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const createTicker = createAsyncThunk(
    "create/ticker",
    async (tickerName: string, { rejectWithValue, getState }) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const body = {
                group_name: groupName,
                ticker_name: tickerName,
                CP: [],
                MDP: [],
                SP: [],
            };
            const response = await instance.post(pathname + "api/ticker", body);
            notifySuccess("Тикер успешно создан");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const addTicker = createAsyncThunk(
    "add/ticker",
    async (tickerName: string, { rejectWithValue, getState }) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const body = {
                group_name: groupName,
                ticker_name: tickerName,
                CP: [
                    [
                        "",
                        "ID.depend",
                        "Cotir.def.param.group.ID",
                        "BidUncrossOffset",
                        "AskUncrossOffset",
                        "VolumeWeight",
                        "NoVolumePenalty",
                        "D.Spread",
                        "NormalSpread",
                        "QuoteVolume",
                        "D_oc",
                        "GO.HardOpen",
                        "moveVol",
                        "movePrice",
                        "eLimitedMargin",
                        "BUY",
                        "SprOpen",
                        "SprClose",
                        "StepPos",
                        "StepSpread",
                        "NoShiftLimit",
                        "LimitBuy",
                        "SELL",
                        "SprOpen",
                        "SprClose",
                        "StepPos",
                        "StepSpread",
                        "NoShiftLimit",
                        "LimitSell",
                        "HEDGING",
                        "IsHedgeble",
                        "Priority",
                        "MinPos",
                        "OrderVol",
                        "PriceDistance",
                        "SpreadCoefDistance",
                        "UseTeorPrices",
                        "HedgeInstId",
                    ],
                    [
                        "1",
                        `${tickerName}~DEP~SPB`,
                        "TEST",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "AAL~HEDGE~SPB",
                    ],
                ],
                MDP: [
                    [
                        "SheduleID",
                        "ID.depend",
                        "ID.source",
                        "Source.Type",
                        "Source.type.shedule.ID",
                    ],
                ],
                SP: [
                    [
                        "ID.source",
                        "Source.def.par.group.ID",
                        "DealsWindow..sec.",
                        "DealsVolRecalcShare",
                        "Currency",
                        "ShortBonus",
                        "NotTradable",
                        "Factor",
                        "Beta",
                        "BaseInstr",
                    ],
                ],
            };

            const response = await instance.post(pathname + "api/ticker", body);
            notifySuccess("Тикер успешно создан");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const saveTicker = createAsyncThunk(
    "save/ticker",
    async (tickerName: string, { rejectWithValue, getState }) => {
        const { settings } = getState() as RootState;
        const { name: groupName } = settings.activeGroup;
        try {
            const body = {
                group_name: groupName,
                ticker_name: tickerName,
                CP: [
                    [
                        "",
                        "ID.depend",
                        "Cotir.def.param.group.ID",
                        "BidUncrossOffset",
                        "AskUncrossOffset",
                        "VolumeWeight",
                        "NoVolumePenalty",
                        "D.Spread",
                        "NormalSpread",
                        "QuoteVolume",
                        "D_oc",
                        "GO.HardOpen",
                        "moveVol",
                        "movePrice",
                        "eLimitedMargin",
                        "BUY",
                        "SprOpen",
                        "SprClose",
                        "StepPos",
                        "StepSpread",
                        "NoShiftLimit",
                        "LimitBuy",
                        "SELL",
                        "SprOpen",
                        "SprClose",
                        "StepPos",
                        "StepSpread",
                        "NoShiftLimit",
                        "LimitSell",
                        "HEDGING",
                        "IsHedgeble",
                        "Priority",
                        "MinPos",
                        "OrderVol",
                        "PriceDistance",
                        "SpreadCoefDistance",
                        "UseTeorPrices",
                        "HedgeInstId",
                    ],
                    [
                        "1",
                        `${tickerName}~DEP~SPB`,
                        "TEST",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "0",
                        "AAL~HEDGE~SPB",
                    ],
                ],
                MDP: [
                    [
                        "SheduleID",
                        "ID.depend",
                        "ID.source",
                        "Source.Type",
                        "Source.type.shedule.ID",
                    ],
                ],
                SP: [
                    [
                        "ID.source",
                        "Source.def.par.group.ID",
                        "DealsWindow..sec.",
                        "DealsVolRecalcShare",
                        "Currency",
                        "ShortBonus",
                        "NotTradable",
                        "Factor",
                        "Beta",
                        "BaseInstr",
                    ],
                ],
            };

            const response = await instance.post(pathname + "api/ticker", body);
            notifySuccess("Тикер успешно создан");
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                return rejectWithValue(e.message);
            }
        }
    }
);

export const deleteTickerFromGroup = createAsyncThunk(
    "delete/tickerFromGroup",
    async (tickerName: string, { rejectWithValue, getState, dispatch }) => {
        const { settings } = getState() as RootState;
        const { name } = settings.activeGroup;
        try {
            const body = {
                group_name: name,
            };
            const response = await instance.delete(
                pathname + `api/ticker/${tickerName}`,
                {
                    data: body,
                }
            );
            notifySuccess(`Тикер ${tickerName} удалён из группы ${name}`);
            dispatch(resetTickerSettings());
            dispatch(removeTickerFromGroup(tickerName));
            return response.data;
        } catch (e) {
            if (axios.isAxiosError(e)) {
                if (e.response.data.block) {
                    const splitted = e.response.data.block[0].split(" ");
                    const user_name = splitted[splitted.length - 1];
                    Error(`Тикер заблокирован пользователем ${user_name}`);
                }
                return rejectWithValue(e.message);
            }
        }
    }
);

const groupsSlice = createSlice({
    name: "GroupsSlice",
    initialState,
    reducers: {
        resetState: () => initialState,
        resetGroupTickers(state) {
            state.activeGroupTickers = initialState.activeGroupTickers;
        },
        addTickerToGroup(state, action) {
            state.activeGroupTickers.push(action.payload);
        },
        removeTickerFromGroup(state, action) {
            state.activeGroupTickers = state.activeGroupTickers.filter(
                (ticker) => ticker.name !== action.payload
            );
        },
    },
    extraReducers: {
        [getGroups.pending.type]: (state) => {
            state.groupsLoading = true;
        },
        [getGroups.rejected.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = action.paylaod;
        },
        [getGroups.fulfilled.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = null;
            state.groups = action.payload;
        },
        [getTickersByGroup.pending.type]: (state) => {
            state.tickersLoading = true;
        },
        [getTickersByGroup.rejected.type]: (state, action) => {
            state.tickersLoading = false;
            state.error = action.paylaod;
        },
        [getTickersByGroup.fulfilled.type]: (state, action) => {
            state.tickersLoading = false;
            state.error = null;
            state.activeGroupTickers = action.payload;
        },
        [getDefaultTickers.fulfilled.type]: (state, action) => {
            state.defaultTickers = action.payload
        },
        [createGroup.pending.type]: (state) => {
            state.groupsLoading = true;
        },
        [createGroup.rejected.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = action.paylaod;
        },
        [createGroup.fulfilled.type]: (state) => {
            state.groupsLoading = false;
            state.error = null;
        },
        [applyGroup.pending.type]: (state) => {
            state.groupsLoading = true;
        },
        [applyGroup.rejected.type]: (state, action) => {
            state.groupsLoading = false;
            state.error = action.paylaod;
        },
        [applyGroup.fulfilled.type]: (state) => {
            state.groupsLoading = false;
            state.error = null;
        },
    },
});

export const { addTickerToGroup, removeTickerFromGroup, resetGroupTickers } =
    groupsSlice.actions;
export default groupsSlice.reducer;
